import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ArrowUp, Minus, Flame, Users, Building2, Ticket, TrendingUp } from "lucide-react";
import { thumbUrl } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmt = (n) => Intl.NumberFormat("en").format(n || 0);

function showDate(raw) {
  const parts = (raw || "").split("-");
  if (parts.length !== 3) return raw;
  const [y, month, day] = parts.map((x) => parseInt(x, 10));
  if (!MONTHS[month - 1]) return raw;
  return `${MONTHS[month - 1]} ${day}, ${y}`;
}

function shortDate(raw) {
  const parts = (raw || "").split("-");
  if (parts.length !== 3) return raw;
  const [, month, day] = parts.map((x) => parseInt(x, 10));
  return MONTHS[month - 1] ? `${MONTHS[month - 1]} ${day}` : raw;
}

function Movement({ delta }) {
  if (delta === null || delta === undefined) {
    return <span data-testid="delta-new" className="rounded bg-[#35606E] px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-white">New</span>;
  }
  if (delta > 0) {
    return (
      <span data-testid="delta-up" className="flex items-center gap-0.5 text-[13px] font-extrabold text-emerald-400">
        <ArrowUp className="h-3.5 w-3.5" strokeWidth={3} />{fmt(delta)}
      </span>
    );
  }
  return <span data-testid="delta-flat" className="flex items-center text-neutral-500"><Minus className="h-3.5 w-3.5" strokeWidth={3} /></span>;
}

function Row({ r, baseline }) {
  const top3 = r.rank <= 3;
  const left = Math.max(r.cap - r.sold, 0);
  const hasBaseline = Boolean(r.baseline);
  const isManual = !hasBaseline && r.as_of && baseline && r.as_of !== baseline;
  let asOfLabel;
  if (hasBaseline) asOfLabel = `as of ${shortDate(r.as_of)}`;
  else asOfLabel = isManual ? `${shortDate(r.as_of)} manual update` : `${shortDate(r.as_of || baseline)} baseline`;
  const gold = hasBaseline || isManual;
  return (
    <div
      data-testid={`row-${r.rank}`}
      className={`flex items-center gap-3 border-b border-white/5 px-3 py-3.5 sm:px-5 ${top3 ? "bg-white/[0.03]" : ""}`}
    >
      <div className="flex w-10 shrink-0 flex-col items-center sm:w-14">
        <span className={`font-display text-2xl font-black leading-none sm:text-3xl ${top3 ? "text-[#E9C46A]" : "text-white"}`}>{r.rank}</span>
        <div className="mt-1"><Movement delta={r.delta} /></div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h3 className="truncate font-display text-lg font-extrabold leading-tight text-white sm:text-xl">
            {r.city}<span className="ml-1.5 text-sm font-semibold text-neutral-400">{r.state}</span>
          </h3>
          {r.sold_out && (
            <span data-testid="sold-out-badge" className="shrink-0 rounded bg-red-600 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-white">Sold out</span>
          )}
        </div>
        <p className="truncate text-xs text-neutral-500">{r.venue} · {showDate(r.date)}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {r.region && (
            <span data-testid={`region-${r.rank}`} className="inline-block rounded bg-[#35606E]/40 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-[#CFE3DE]">
              {r.region}
            </span>
          )}
          <span
            data-testid={`asof-${r.rank}`}
            className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${gold ? "bg-[#E9C46A] text-black" : "bg-white/10 text-neutral-400"}`}
          >
            {asOfLabel}
          </span>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
            <div
              className={`h-full rounded-full ${r.sold_out ? "bg-red-500" : "bg-[#4CA1A3]"}`}
              style={{ width: `${Math.min(100, r.pct)}%` }}
            />
          </div>
          <span className="w-12 shrink-0 text-right text-[11px] font-bold tabular-nums text-neutral-400">{r.pct.toFixed(0)}%</span>
        </div>
      </div>

      <div className="shrink-0 text-right">
        <div className="font-display text-2xl font-black leading-none tabular-nums text-white sm:text-3xl">{fmt(r.sold)}</div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">of {fmt(r.cap)} cap</div>
        <div data-testid={`left-${r.rank}`} className="mt-0.5 text-[11px] font-bold tabular-nums text-[#4CA1A3]">{fmt(left)} left</div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="flex flex-col items-center gap-1 px-2 text-center">
      <Icon className="h-4 w-4 text-[#4CA1A3]" />
      <div className="font-display text-xl font-black leading-none text-white sm:text-2xl">{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{label}</div>
    </div>
  );
}

export default function TourLeaderboard() {
  const { slug = "akeem-ali" } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/tour/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="grid min-h-screen place-items-center bg-[#0a0a0b] text-neutral-400">Leaderboard unavailable.</div>;
  if (!data) return <div className="grid min-h-screen place-items-center bg-[#0a0a0b]"><div className="h-8 w-8 animate-spin rounded-full border-2 border-white/20 border-t-[#4CA1A3]" /></div>;

  const s = data.summary;

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white" data-testid="tour-leaderboard">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <header className="relative overflow-hidden border-b border-white/10 px-5 pb-6 pt-8">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#35606E]/25 blur-3xl" />
          <div className="relative flex items-center gap-4">
            {data.image && (
              <img data-testid="tour-artist-image" src={thumbUrl(data.image, 240)} alt={data.artist} width={80} height={80} loading="lazy" decoding="async" className="h-16 w-16 shrink-0 rounded-2xl object-cover ring-2 ring-white/10 sm:h-20 sm:w-20" />
            )}
            <div className="min-w-0">
              <p className="font-display text-xs font-black uppercase tracking-[0.2em] text-[#4CA1A3]">{data.artist} · {data.tour}</p>
              <h1 className="font-display text-3xl font-black uppercase leading-none tracking-tight sm:text-5xl">City Leaderboard</h1>
            </div>
          </div>
          {data.baselines ? (
            <p className="relative mt-3 text-xs text-neutral-400 sm:text-sm">
              All {s.cities} cities ranked by tickets sold. Counts as of{" "}
              {Object.entries(data.baselines).map(([region, b], i) => (
                <span key={region}>
                  {i > 0 && <span className="text-neutral-600"> | </span>}
                  <span className="font-semibold text-[#E9C46A]">{region}: {showDate(b.as_of)}</span>
                  <span className="text-neutral-500"> vs {showDate(b.baseline)} baseline</span>
                </span>
              ))}
              . Δ is change since each region's baseline (not a daily change).
            </p>
          ) : (
            <p className="relative mt-3 text-xs text-neutral-400 sm:text-sm">
              All {s.cities} cities ranked by tickets sold · {data.baseline_date ? `${showDate(data.baseline_date)} baseline` : `updated ${showDate(data.updated)}`}
              {data.baseline_date && data.updated !== data.baseline_date && (
                <span className="text-[#E9C46A]"> · latest update {showDate(data.updated)}</span>
              )}
            </p>
          )}
        </header>

        {/* Summary strip */}
        <section data-testid="summary-strip" className="grid grid-cols-4 gap-1 border-b border-white/10 bg-white/[0.02] py-4">
          <Stat icon={Ticket} value={fmt(s.tickets_sold)} label="Tickets" />
          <Stat icon={Building2} value={s.cities} label="Cities" />
          <Stat icon={Flame} value={s.sold_out} label="Sold out" />
          <Stat icon={TrendingUp} value={`${s.pct_of_capacity}%`} label="Capacity" />
        </section>

        {/* Rows */}
        <main>
          {data.rows.map((r) => <Row key={r.rank} r={r} baseline={data.baseline_date} />)}
        </main>

        {/* Footer */}
        <footer className="px-5 py-8 text-center">
          <p className="text-[11px] text-neutral-500">Data: official ticket count report, updated as new counts come in</p>
          <div className="mt-3 flex items-center justify-center gap-1.5 opacity-40">
            <span className="grid h-5 w-5 place-items-center rounded-md border border-white/30 font-display text-xs font-black">P</span>
            <span className="font-display text-[11px] font-bold uppercase tracking-[0.2em]">Playbook Distro</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
